import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { Image, Mic, Video, Sticker, FileText, Activity, TrendingUp, AlertTriangle } from "lucide-react";

// ── Painel KPI Multimodal ───────────────────────────────────────────
// Lê multimodal_metrics_hourly (24h) para taxas por tipo,
// multimodal_metrics (24h) para multimodal_hits efetivos,
// multimodal_descriptions (7d) para evidências por grupo.

type KindMeta = { icon: typeof Image; color: string; label: string };
const KIND_META: Record<string, KindMeta> = {
  image: { icon: Image, color: "bg-blue-500/10 text-blue-700 dark:text-blue-300", label: "Imagem" },
  audio: { icon: Mic, color: "bg-purple-500/10 text-purple-700 dark:text-purple-300", label: "Áudio" },
  video: { icon: Video, color: "bg-red-500/10 text-red-700 dark:text-red-300", label: "Vídeo" },
  sticker: { icon: Sticker, color: "bg-amber-500/10 text-amber-700 dark:text-amber-300", label: "Figurinha" },
  document: { icon: FileText, color: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300", label: "Documento" },
  link: { icon: FileText, color: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300", label: "Link" },
};

type HourlyRow = {
  kind: string;
  total: number;
  ok: number;
  errors: number;
  native_used: number;
  rejected_total: number;
  avg_latency_ms: number | null;
  p95_latency_ms: number | null;
};

type MetricRow = {
  group_id: string | null;
  kind: string;
  multimodal_used: boolean;
  status: string | null;
};

type GroupAggRow = {
  group_id: string;
  group_name: string | null;
  evidence_count: number;
  by_kind: Record<string, number>;
};

type KindStats = {
  total: number;
  ok: number;
  errors: number;
  rejected: number;
  native_used: number;
  ok_rate: number;
  native_rate: number;
  multimodal_hits: number;
  hit_rate: number;
  avg_latency_ms: number | null;
  p95_latency_ms: number | null;
};

export default function MultimodalKpiPanel() {
  const [loading, setLoading] = useState(true);
  const [hourly, setHourly] = useState<HourlyRow[]>([]);
  const [hits, setHits] = useState<MetricRow[]>([]);
  const [groups, setGroups] = useState<GroupAggRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const since24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const since7d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      // 1) Taxas agregadas por tipo nas últimas 24h
      const { data: hourlyData } = await supabase
        .from("multimodal_metrics_hourly")
        .select("kind, total, ok, errors, native_used, rejected_total, avg_latency_ms, p95_latency_ms")
        .gte("hour", since24h);

      // 2) Hits efetivos (multimodal_used=true) — turn-level
      const { data: metricsData } = await supabase
        .from("multimodal_metrics")
        .select("group_id, kind, multimodal_used, status")
        .gte("created_at", since24h)
        .limit(5000);

      // 3) Evidências por grupo (7d)
      const { data: descData } = await supabase
        .from("multimodal_descriptions")
        .select("group_id, kind")
        .gte("created_at", since7d)
        .not("group_id", "is", null)
        .limit(5000);

      // Resolve nomes de grupo (tabela fora do schema tipado → cast para any)
      const groupIds = Array.from(new Set((descData ?? []).map((r: any) => r.group_id).filter(Boolean)));
      let nameMap: Record<string, string> = {};
      if (groupIds.length > 0) {
        const { data: groupsData } = await (supabase as any)
          .from("whatsapp_groups")
          .select("id, name")
          .in("id", groupIds);
        nameMap = Object.fromEntries(((groupsData ?? []) as any[]).map((g: any) => [g.id, g.name ?? "(sem nome)"]));
      }

      // Agrega por grupo
      const byGroup: Record<string, GroupAggRow> = {};
      (descData ?? []).forEach((r: any) => {
        const gid = r.group_id as string;
        if (!byGroup[gid]) {
          byGroup[gid] = { group_id: gid, group_name: nameMap[gid] ?? null, evidence_count: 0, by_kind: {} };
        }
        byGroup[gid].evidence_count += 1;
        byGroup[gid].by_kind[r.kind] = (byGroup[gid].by_kind[r.kind] ?? 0) + 1;
      });
      const topGroups = Object.values(byGroup)
        .sort((a, b) => b.evidence_count - a.evidence_count)
        .slice(0, 10);

      if (cancelled) return;
      setHourly((hourlyData ?? []) as HourlyRow[]);
      setHits((metricsData ?? []) as MetricRow[]);
      setGroups(topGroups);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const statsByKind = useMemo<Record<string, KindStats>>(() => {
    const acc: Record<string, KindStats> = {};
    // Base: hourly (engloba toda atividade do tipo)
    for (const row of hourly) {
      if (!acc[row.kind]) {
        acc[row.kind] = {
          total: 0, ok: 0, errors: 0, rejected: 0, native_used: 0,
          ok_rate: 0, native_rate: 0, multimodal_hits: 0, hit_rate: 0,
          avg_latency_ms: null, p95_latency_ms: null,
        };
      }
      const s = acc[row.kind];
      s.total += Number(row.total ?? 0);
      s.ok += Number(row.ok ?? 0);
      s.errors += Number(row.errors ?? 0);
      s.rejected += Number(row.rejected_total ?? 0);
      s.native_used += Number(row.native_used ?? 0);
      // Latência: pega o maior p95 entre as horas como pior caso
      if (row.p95_latency_ms != null) {
        s.p95_latency_ms = Math.max(s.p95_latency_ms ?? 0, Number(row.p95_latency_ms));
      }
      if (row.avg_latency_ms != null) {
        s.avg_latency_ms = Math.round(((s.avg_latency_ms ?? 0) + Number(row.avg_latency_ms)) / 2);
      }
    }
    // Hits: multimodal_used=true em multimodal_metrics
    for (const row of hits) {
      const k = row.kind;
      if (!acc[k]) {
        acc[k] = {
          total: 0, ok: 0, errors: 0, rejected: 0, native_used: 0,
          ok_rate: 0, native_rate: 0, multimodal_hits: 0, hit_rate: 0,
          avg_latency_ms: null, p95_latency_ms: null,
        };
      }
      if (row.multimodal_used) acc[k].multimodal_hits += 1;
    }
    // Calcula taxas
    for (const k of Object.keys(acc)) {
      const s = acc[k];
      s.ok_rate = s.total > 0 ? Math.round((s.ok / s.total) * 1000) / 10 : 0;
      s.native_rate = s.total > 0 ? Math.round((s.native_used / s.total) * 1000) / 10 : 0;
      s.hit_rate = s.total > 0 ? Math.round((s.multimodal_hits / s.total) * 1000) / 10 : 0;
    }
    return acc;
  }, [hourly, hits]);

  const totalHits = useMemo(
    () => Object.values(statsByKind).reduce((sum, s) => sum + s.multimodal_hits, 0),
    [statsByKind]
  );
  const totalEvents = useMemo(
    () => Object.values(statsByKind).reduce((sum, s) => sum + s.total, 0),
    [statsByKind]
  );
  const overallHitRate = totalEvents > 0 ? Math.round((totalHits / totalEvents) * 1000) / 10 : 0;
  const totalErrors = useMemo(
    () => Object.values(statsByKind).reduce((sum, s) => sum + s.errors + s.rejected, 0),
    [statsByKind]
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const kinds = Object.keys(statsByKind).length > 0
    ? Object.keys(statsByKind)
    : Object.keys(KIND_META);

  return (
    <div className="space-y-6">
      {/* KPIs gerais 24h */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-md bg-primary/10 text-primary">
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <div className="text-2xl font-bold">{totalHits}</div>
              <div className="text-xs text-muted-foreground">multimodal_hits (24h)</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-md bg-primary/10 text-primary">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <div className="text-2xl font-bold">{overallHitRate}%</div>
              <div className="text-xs text-muted-foreground">
                taxa de hit geral · {totalEvents} eventos
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-md bg-destructive/10 text-destructive">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <div className="text-2xl font-bold">{totalErrors}</div>
              <div className="text-xs text-muted-foreground">erros + rejeições (24h)</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Taxa de sucesso por tipo */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Taxa de sucesso por tipo (últimas 24h)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {kinds.map((kind) => {
              const s = statsByKind[kind] ?? {
                total: 0, ok: 0, errors: 0, rejected: 0, native_used: 0,
                ok_rate: 0, native_rate: 0, multimodal_hits: 0, hit_rate: 0,
                avg_latency_ms: null, p95_latency_ms: null,
              };
              const meta = KIND_META[kind] ?? KIND_META.image;
              const Icon = meta.icon;
              return (
                <div key={kind} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded ${meta.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-sm">{meta.label}</span>
                      <Badge variant="outline" className="text-xs">
                        {s.total} eventos
                      </Badge>
                      {s.multimodal_hits > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {s.multimodal_hits} hits
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      {s.errors > 0 && <span className="text-destructive">{s.errors} erros</span>}
                      {s.rejected > 0 && <span>{s.rejected} rejeitados</span>}
                      {s.p95_latency_ms != null && <span>p95 {s.p95_latency_ms}ms</span>}
                      <span className="font-semibold text-foreground">{s.ok_rate}% OK</span>
                    </div>
                  </div>
                  <Progress value={s.ok_rate} className="h-2" />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Top grupos por evidências multimodais (7d) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Evidências multimodais por grupo (últimos 7 dias)</CardTitle>
        </CardHeader>
        <CardContent>
          {groups.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhuma evidência multimodal nos últimos 7 dias.
            </p>
          ) : (
            <ScrollArea className="max-h-[320px]">
              <div className="space-y-2">
                {groups.map((g) => (
                  <div
                    key={g.group_id}
                    className="flex items-center justify-between gap-3 border rounded-md p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">
                        {g.group_name ?? g.group_id.slice(0, 8)}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Object.entries(g.by_kind).map(([k, n]) => {
                          const meta = KIND_META[k] ?? KIND_META.image;
                          return (
                            <Badge key={k} variant="outline" className="text-xs">
                              {meta.label}: {n}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                    <div className="text-2xl font-bold tabular-nums">{g.evidence_count}</div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
